import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Copy, FileText } from "lucide-react";
import { api, assetUrl } from "../../lib/api";

export default function AdminMedia() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/admin/media").then(({ data }) => setFiles(data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/upload", fd);
      toast.success("File uploaded to cloud storage");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const copyUrl = (f) => {
    navigator.clipboard.writeText(assetUrl(f.url));
    toast.success("URL copied — paste it into any image field");
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.original_filename}"?`)) return;
    await api.delete(`/admin/media/${f.id}`);
    toast.success("File deleted");
    load();
  };

  const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

  return (
    <div data-testid="admin-media-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Media Library</h1>
          <p className="text-sm text-slate-500 mt-1">{files.length} files · stored in cloud object storage</p>
        </div>
        <button data-testid="upload-media-button" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 active:scale-95 disabled:opacity-50 transition-colors duration-200">
          <Upload className="w-4 h-4" aria-hidden="true" /> {uploading ? "Uploading…" : "Upload file"}
        </button>
        <input ref={inputRef} type="file" accept="image/*,.pdf" className="hidden" onChange={upload} data-testid="media-file-input" />
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {files.map((f) => (
          <div key={f.id} data-testid={`media-item-${f.id}`} className="bg-white rounded-lg border border-border overflow-hidden group">
            <div className="h-32 bg-slate-100 flex items-center justify-center overflow-hidden">
              {f.content_type?.startsWith("image/") ? (
                <img src={assetUrl(f.url)} alt={f.original_filename} className="w-full h-full object-contain" loading="lazy" />
              ) : (
                <FileText className="w-10 h-10 text-slate-300" aria-hidden="true" />
              )}
            </div>
            <div className="p-3">
              <p className="text-xs font-semibold text-slate-700 truncate" title={f.original_filename}>{f.original_filename}</p>
              <p className="text-xs text-slate-400 mt-0.5">{fmtSize(f.size)}</p>
              <div className="mt-2 flex gap-1">
                <button data-testid={`copy-url-${f.id}`} onClick={() => copyUrl(f)} title="Copy URL"
                  className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-border text-xs font-semibold text-slate-600 hover:border-sky-400 hover:text-sky-600 transition-colors duration-200">
                  <Copy className="w-3 h-3" aria-hidden="true" /> Copy URL
                </button>
                <button data-testid={`delete-media-${f.id}`} onClick={() => remove(f)} title="Delete"
                  className="p-1.5 rounded border border-border text-rose-500 hover:bg-rose-50 transition-colors duration-200">
                  <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {files.length === 0 && (
          <p className="col-span-full text-center py-16 text-slate-400" data-testid="media-empty-message">
            No files yet. Upload vendor logos, article covers, or PDF templates.
          </p>
        )}
      </div>
    </div>
  );
}
